import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { transitionLeadStatus } from '@/api/leads.api';
import { ApiError } from '@/api/errors';
import { StatusChip } from '@/components/common/StatusChip';
import { showSuccessToast } from '@/lib/toast';
import { formatDateTime, formatDate } from '@/lib/format';
import { allowedNextStatuses, subFieldFor, toneForLeadStatus } from '@/lib/leadStatus';
import { LEAD_STATUSES } from '@/types/shared';
import type { LeadResponse, LeadStatus } from '@/types/shared';
import './LeadPanels.css';

interface FieldErrors {
  appointmentDateTime?: string;
  lostReason?: string;
  nextFollowUpDate?: string;
}

interface LeadStatusPanelProps {
  lead: LeadResponse;
  /** Sales Manager/Admin — the only roles allowed to reopen a `Lost` lead back to
   * `Follow-up Later` (03-rbac-and-lifecycle.md §3, sales.service.ts `transitionStatus`). */
  canReopenLost: boolean;
  onSaved: (updated: LeadResponse) => void;
}

/** The status-transition control — offers only the next statuses the state machine (and this
 * user's role) actually allow from the lead's current status, and collects whichever sub-field the
 * chosen target requires (`lib/leadStatus.ts` mirrors `sales.service.ts`'s `TRANSITIONS`/guard
 * rules exactly). `Registered` never appears here — it's reachable only via Convert. */
export function LeadStatusPanel({ lead, canReopenLost, onSaved }: LeadStatusPanelProps) {
  const { t, i18n } = useTranslation();
  const allowed = allowedNextStatuses(lead.status, canReopenLost);

  const [targetStatus, setTargetStatus] = useState<LeadStatus | ''>('');
  const [appointmentDateTime, setAppointmentDateTime] = useState('');
  const [lostReason, setLostReason] = useState('');
  const [nextFollowUpDate, setNextFollowUpDate] = useState('');
  const [note, setNote] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [panelError, setPanelError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const subField = targetStatus ? subFieldFor(targetStatus) : null;

  function resetForm() {
    setTargetStatus('');
    setAppointmentDateTime('');
    setLostReason('');
    setNextFollowUpDate('');
    setNote('');
    setFieldErrors({});
  }

  function validate(): FieldErrors {
    const errors: FieldErrors = {};
    if (subField === 'appointmentDateTime' && !appointmentDateTime) {
      errors.appointmentDateTime = t('leads.detail.errors.appointmentDateTimeRequired');
    }
    if (subField === 'lostReason' && !lostReason.trim()) {
      errors.lostReason = t('leads.detail.errors.lostReasonRequired');
    }
    if (subField === 'nextFollowUpDate' && !nextFollowUpDate) {
      errors.nextFollowUpDate = t('leads.detail.errors.nextFollowUpDateRequired');
    }
    return errors;
  }

  async function handleSubmit() {
    if (!targetStatus) return;
    setPanelError(null);
    setSavedFlash(false);
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    try {
      const updated = await transitionLeadStatus(lead.leadId, {
        status: targetStatus,
        appointmentDateTime:
          subField === 'appointmentDateTime' && appointmentDateTime
            ? new Date(appointmentDateTime).toISOString()
            : undefined,
        lostReason: subField === 'lostReason' ? lostReason.trim() : undefined,
        nextFollowUpDate: subField === 'nextFollowUpDate' ? nextFollowUpDate : undefined,
        note: note.trim() || undefined,
      });
      onSaved(updated);
      resetForm();
      setSavedFlash(true);
      showSuccessToast(t('leads.detail.transitionSuccess'));
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      const errs: FieldErrors = {};
      for (const detail of apiError.details) {
        if (
          detail.field === 'appointmentDateTime' ||
          detail.field === 'lostReason' ||
          detail.field === 'nextFollowUpDate'
        ) {
          errs[detail.field] = detail.message;
        }
      }
      if (Object.keys(errs).length > 0) {
        setFieldErrors(errs);
      } else if (apiError.code === 'FORBIDDEN') {
        setPanelError(t('leads.detail.errors.reopenLostForbidden'));
      } else if (apiError.code === 'INVALID_STATUS_TRANSITION') {
        setPanelError(t('leads.detail.errors.invalidTransition'));
      } else {
        setPanelError(apiError.message || t('leads.detail.errors.transitionFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  const hasStatusDetails = Boolean(
    lead.appointmentDateTime || lead.attendanceResult || lead.lostReason || lead.nextFollowUpDate,
  );

  return (
    <section className="fa-form-section fa-lead-panel">
      <h2 className="fa-form-section__title">{t('leads.detail.statusTitle')}</h2>

      <div className="fa-lead-panel__current-status">
        <StatusChip
          label={t(`leads.status.${lead.status}`)}
          tone={toneForLeadStatus(lead.status)}
        />
      </div>

      {hasStatusDetails && (
        <dl className="fa-detail-list fa-lead-panel__status-details">
          {lead.appointmentDateTime && (
            <div className="fa-detail-row">
              <dt>{t('leads.detail.appointmentAt')}</dt>
              <dd>
                <bdi>{formatDateTime(lead.appointmentDateTime, i18n.language)}</bdi>
              </dd>
            </div>
          )}
          {lead.attendanceResult && (
            <div className="fa-detail-row">
              <dt>{t('leads.detail.attendanceResultLabel')}</dt>
              <dd>{t(`leads.status.${lead.attendanceResult}`)}</dd>
            </div>
          )}
          {lead.lostReason && (
            <div className="fa-detail-row">
              <dt>{t('leads.detail.lostReasonLabel')}</dt>
              <dd>{lead.lostReason}</dd>
            </div>
          )}
          {lead.nextFollowUpDate && (
            <div className="fa-detail-row">
              <dt>{t('leads.detail.nextFollowUpLabel')}</dt>
              <dd>
                <bdi>{formatDate(lead.nextFollowUpDate, i18n.language)}</bdi>
              </dd>
            </div>
          )}
        </dl>
      )}

      {allowed.length === 0 ? (
        <p className="fa-field__hint">
          {lead.status === 'Lost'
            ? t('leads.detail.reopenLostHint')
            : t('leads.detail.noTransitionsHint')}
        </p>
      ) : (
        <div className="fa-lead-panel__transition">
          <div className="fa-field-row">
            <div className="fa-field">
              <label htmlFor="lead-nextStatus">{t('leads.detail.nextStatusLabel')}</label>
              <select
                id="lead-nextStatus"
                value={targetStatus}
                disabled={submitting}
                onChange={(e) => {
                  setPanelError(null);
                  setSavedFlash(false);
                  setFieldErrors({});
                  setTargetStatus(e.target.value as LeadStatus | '');
                }}
              >
                <option value="">{t('leads.detail.nextStatusPlaceholder')}</option>
                {LEAD_STATUSES.filter((status) => allowed.includes(status)).map((status) => (
                  <option key={status} value={status}>
                    {t(`leads.status.${status}`)}
                  </option>
                ))}
              </select>
            </div>

            {subField === 'appointmentDateTime' && (
              <div className="fa-field">
                <label htmlFor="lead-appointmentDateTime">
                  {t('leads.detail.appointmentDateTime')}
                  <span className="fa-field__required" aria-hidden="true">
                    {' '}
                    *
                  </span>
                </label>
                <input
                  id="lead-appointmentDateTime"
                  type="datetime-local"
                  value={appointmentDateTime}
                  disabled={submitting}
                  aria-invalid={Boolean(fieldErrors.appointmentDateTime)}
                  onChange={(e) => setAppointmentDateTime(e.target.value)}
                />
                {fieldErrors.appointmentDateTime && (
                  <p className="fa-field__error">{fieldErrors.appointmentDateTime}</p>
                )}
              </div>
            )}

            {subField === 'lostReason' && (
              <div className="fa-field">
                <label htmlFor="lead-lostReason">
                  {t('leads.detail.lostReason')}
                  <span className="fa-field__required" aria-hidden="true">
                    {' '}
                    *
                  </span>
                </label>
                <input
                  id="lead-lostReason"
                  type="text"
                  maxLength={255}
                  value={lostReason}
                  disabled={submitting}
                  aria-invalid={Boolean(fieldErrors.lostReason)}
                  onChange={(e) => setLostReason(e.target.value)}
                />
                {fieldErrors.lostReason && (
                  <p className="fa-field__error">{fieldErrors.lostReason}</p>
                )}
              </div>
            )}

            {subField === 'nextFollowUpDate' && (
              <div className="fa-field">
                <label htmlFor="lead-nextFollowUpDate">
                  {t('leads.detail.nextFollowUpDate')}
                  <span className="fa-field__required" aria-hidden="true">
                    {' '}
                    *
                  </span>
                </label>
                <input
                  id="lead-nextFollowUpDate"
                  type="date"
                  value={nextFollowUpDate}
                  disabled={submitting}
                  aria-invalid={Boolean(fieldErrors.nextFollowUpDate)}
                  onChange={(e) => setNextFollowUpDate(e.target.value)}
                />
                {fieldErrors.nextFollowUpDate && (
                  <p className="fa-field__error">{fieldErrors.nextFollowUpDate}</p>
                )}
              </div>
            )}
          </div>

          {targetStatus && (
            <div className="fa-field">
              <label htmlFor="lead-transitionNote">{t('leads.detail.transitionNote')}</label>
              <textarea
                id="lead-transitionNote"
                placeholder={t('leads.detail.transitionNotePlaceholder')}
                value={note}
                disabled={submitting}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          )}

          <div className="fa-lead-panel__actions">
            <button
              type="button"
              className="fa-btn fa-btn--primary"
              disabled={!targetStatus || submitting}
              onClick={handleSubmit}
            >
              {submitting ? t('leads.detail.transitioning') : t('leads.detail.transitionSubmit')}
            </button>
            {savedFlash && (
              <span className="fa-lead-panel__saved" role="status">
                {t('leads.detail.transitionSuccess')}
              </span>
            )}
            {panelError && (
              <p className="fa-field__error" role="alert">
                {panelError}
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
